// Detects environments on iOS/Android where navigator.mediaDevices.getUserMedia
// will silently fail with "This site cannot grant access to the microphone".
//
// The three culprits (in observed order of frequency on production):
//   1. In-app browsers (Facebook, Instagram, LinkedIn, TikTok, X/Twitter,
//      Snapchat, Messenger, WhatsApp, Gmail, Line, WeChat, Threads, Pinterest).
//      Their WKWebView shell cannot grant mic access on iOS — period.
//   2. Site embedded inside an <iframe> without an `allow="microphone"` attribute
//      on the parent. Happens with some link-previewers / staging environments.
//   3. iOS "Add to Home Screen" standalone launch on very old iOS builds.
//
// We only return a *reason* — the UI layer decides how to present it.

const UA = () => (typeof navigator !== 'undefined' ? navigator.userAgent || '' : '');

export const isEdge = () => /Edg\//i.test(UA());     // Chromium-based Edge
export const isChrome = () => /Chrome\//i.test(UA()) && !/Edg\//i.test(UA()) && !/OPR\//i.test(UA());
export const isFirefox = () => /Firefox\//i.test(UA());

export const isIOS = () => /iPad|iPhone|iPod/i.test(UA()) && !/(CriOS|FxiOS|EdgiOS)/i.test(UA())
  ? true
  : /iPad|iPhone|iPod/i.test(UA()); // include Chrome/Firefox on iOS too

export const isAndroid = () => /Android/i.test(UA());

export const isMobile = () => isIOS() || isAndroid();

// Every in-app browser we've seen block getUserMedia. Order matters — some
// UAs contain multiple keywords (e.g. Instagram appends "FBAV").
const IN_APP_SIGNATURES = [
  { key: 'Instagram', name: 'Instagram' },
  { key: 'FBAN', name: 'Facebook' },
  { key: 'FBAV', name: 'Facebook' },
  { key: 'FB_IAB', name: 'Facebook' },
  { key: 'FB4A', name: 'Facebook' },
  { key: 'Messenger', name: 'Messenger' },
  { key: 'MessengerLiteForiOS', name: 'Messenger' },
  { key: 'LinkedInApp', name: 'LinkedIn' },
  { key: 'Twitter', name: 'X (Twitter)' },
  { key: 'TwitterAndroid', name: 'X (Twitter)' },
  { key: 'Snapchat', name: 'Snapchat' },
  { key: 'TikTok', name: 'TikTok' },
  { key: 'musical_ly', name: 'TikTok' },
  { key: 'BytedanceWebview', name: 'TikTok' },
  { key: 'Line', name: 'LINE' },
  { key: 'MicroMessenger', name: 'WeChat' },
  { key: 'WhatsApp', name: 'WhatsApp' },
  { key: 'Threads', name: 'Threads' },
  { key: 'Pinterest', name: 'Pinterest' },
  { key: 'GSA/', name: 'Google-appen' },
  { key: 'Gmail', name: 'Gmail-appen' },
];

export const detectInAppBrowser = () => {
  const ua = UA();
  for (const sig of IN_APP_SIGNATURES) {
    if (ua.includes(sig.key)) return sig.name;
  }
  return null;
};

export const isStandalonePWA = () => {
  if (typeof window === 'undefined') return false;
  // iOS Safari
  if (window.navigator && window.navigator.standalone === true) return true;
  // Standard
  try {
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  } catch { /* noop */ }
  return false;
};

export const isEmbeddedInIframe = () => {
  try {
    return typeof window !== 'undefined' && window.self !== window.top;
  } catch {
    // Cross-origin frame access throws — that itself confirms iframe embedding.
    return true;
  }
};

/**
 * Returns { blocked: bool, reason: string, actionable: string, appName?: string }
 * `actionable` is a Danish user-facing instruction on how to proceed.
 */
export const diagnoseMicSupport = () => {
  // 1) HTTPS is required (mediaDevices is not exposed on http://)
  if (typeof window !== 'undefined'
      && window.location
      && window.location.protocol !== 'https:'
      && window.location.hostname !== 'localhost') {
    return {
      blocked: true,
      reason: 'insecure',
      actionable: 'Denne side skal indlæses via HTTPS for at få adgang til mikrofonen. Åbn https:// versionen af siden.',
    };
  }

  // 2) mediaDevices completely missing → almost always an in-app WebView
  if (typeof navigator === 'undefined'
      || !navigator.mediaDevices
      || typeof navigator.mediaDevices.getUserMedia !== 'function') {
    const app = detectInAppBrowser();
    return {
      blocked: true,
      reason: app ? 'in-app' : 'no-media-api',
      appName: app || undefined,
      actionable: app
        ? `Du åbnede linket inde i ${app}-appens indbyggede browser. Denne kan ikke give adgang til mikrofonen. Tryk på "…" eller "del"-knappen og vælg "Åbn i Safari" (iPhone) eller "Åbn i Chrome" (Android).`
        : 'Din browser understøtter ikke mikrofon-adgang. Åbn siden i Safari (iPhone) eller Chrome (Android).',
    };
  }

  // 3) In-app browser detected via UA (mediaDevices might exist but calls fail)
  const app = detectInAppBrowser();
  if (app) {
    return {
      blocked: true,
      reason: 'in-app',
      appName: app,
      actionable: `Du åbnede linket inde i ${app}-appens indbyggede browser. Denne kan ikke give adgang til mikrofonen. Tryk på "…" eller "del"-knappen og vælg "Åbn i Safari" (iPhone) eller "Åbn i Chrome" (Android).`,
    };
  }

  // 4) Cross-origin iframe with no allow="microphone" — hard to detect for sure
  // without probing, but we can WARN (not block) here.
  if (isEmbeddedInIframe()) {
    return {
      blocked: false,
      reason: 'iframe',
      actionable: 'Siden er indlejret i en iframe. Hvis mikrofonen ikke virker, så åbn siden direkte i browseren i stedet for via forhåndsvisningen.',
    };
  }

  return { blocked: false, reason: 'ok', actionable: '' };
};

/**
 * Async pre-check via Permissions API. Distinguishes:
 *   - "granted": browser already has mic permission → getUserMedia will succeed
 *   - "prompt":  browser will show the permission popup → normal path
 *   - "denied":  user or admin has permanently blocked mic for this origin
 *                → we can show the exact "unblock" instructions BEFORE calling
 *                  getUserMedia (which would otherwise throw NotAllowedError
 *                  and confuse the user).
 * Returns "unknown" on browsers that don't support Permissions API for mic
 * (Safari < 16, some older Firefox); caller should just fall through to
 * getUserMedia in that case.
 */
export const queryMicPermission = async () => {
  try {
    if (!navigator.permissions || typeof navigator.permissions.query !== 'function') {
      return 'unknown';
    }
    const status = await navigator.permissions.query({ name: 'microphone' });
    return status.state; // 'granted' | 'prompt' | 'denied'
  } catch {
    return 'unknown';
  }
};

/**
 * Browser-specific instructions for re-enabling a blocked microphone.
 * Called after we know permission is `denied` (or NotAllowedError was thrown).
 */
export const getUnblockInstructions = () => {
  if (isEdge()) {
    return {
      title: 'Mikrofon er blokeret i Microsoft Edge',
      message:
        'Edge har husket at mikrofonen er blokeret for dette site. Sådan ophæver du det:\n\n' +
        '1) Klik på hængelåsen eller ikonet til venstre for adressen (samtale24-7.dk).\n' +
        '2) Vælg "Site-tilladelser" → Mikrofon → sæt til "Tillad".\n' +
        '3) Genindlæs siden og tryk på mic-knappen igen.\n\n' +
        'Virker det stadig ikke? Åbn edge://settings/content/microphone og fjern samtale24-7.dk fra "Bloker"-listen. ' +
        'Tjek også Windows: Indstillinger → Privatliv & sikkerhed → Mikrofon → slå "Lad desktop-apps få adgang" TIL.',
    };
  }
  if (isChrome()) {
    return {
      title: 'Mikrofon er blokeret i Chrome',
      message:
        'Chrome har husket at mikrofonen er blokeret for dette site.\n\n' +
        '1) Klik på hængelåsen til venstre for adressen.\n' +
        '2) Vælg Mikrofon → "Tillad".\n' +
        '3) Genindlæs siden.',
    };
  }
  if (isFirefox()) {
    return {
      title: 'Mikrofon er blokeret i Firefox',
      message:
        '1) Klik på hængelåsen til venstre for adressen.\n' +
        '2) Fjern blokeringen for "Brug mikrofonen".\n' +
        '3) Genindlæs siden og tryk "Tillad" når Firefox spørger.',
    };
  }
  if (isIOS()) {
    return {
      title: 'Mikrofon er blokeret på iOS',
      message:
        '1) Åbn iOS-indstillinger → Safari → Mikrofon → "Spørg" eller "Tillad".\n' +
        '2) Gå tilbage til Safari og genindlæs siden.\n' +
        '3) Tryk "Tillad" når iOS spørger.',
    };
  }
  return {
    title: 'Mikrofon-adgang blev afvist',
    message: 'Åbn browser-indstillingerne, tillad mikrofon for samtale24-7.dk, og genindlæs siden.',
  };
};
